import { defaultDateTimeFormat, defaultServerDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { combineStrings } from 'assets/utils/data/String';
import dayjs from 'dayjs';
import Application from 'models/core/application/Model.Application';
import MediaFile from 'models/core/mediaFile/Model.MediaFile';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Model from 'models/Model';
import Contract from '../contract/Model.Contract';
import statementBalanceListApi from './list/Api.StatementBalance.List';
import statementBalanceSaveApi from './save/Api.StatementBalance.Save';
import statementBalanceDeleteApi from './delete/Api.StatementBalance.Delete';
import { getLocales } from 'assets/locales/Locale';

export default class StatementBalance
  extends Model<Model.IStatementBalance>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IStatementBalance>
{
  id: number;
  statementBalanceNumber: number;
  status: Model.Enum.StatementBalanceStatusCode;
  totalAmountDueCAD: number;
  invoicingDate: string;
  fromDate: string;
  toDate: string;
  createdAtUtc: string;
  createdAtLocal: string;
  modifiedAtUtc: string;
  paidOnLocalDate?: string;
  representativeId: number;
  representative: User;
  dealerId: number;
  dealer: Dealer;
  contracts: Contract[];
  isEmailSent?: boolean;
  createdById?: number;
  createdBy?: User;
  modifiedById?: number;
  modifiedBy?: User;
  applicationId?: number;
  application?: Application;
  mediaFiles?: MediaFile[];

  constructor(
    data: Partial<Model.IStatementBalance> | Utils.FormData<Model.IStatementBalance>,
    language?: Locale.Languages
  ) {
    super(
      {
        representative: User,
        dealer: Dealer,
        createdBy: User,
        modifiedBy: User,
        application: Application,
        contracts: [Contract],
        mediaFiles: [MediaFile],
      },
      language
    );
    this.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    const { lang } = getLocales();
    const fromDate = dayjs(this.fromDate, defaultServerDateTimeFormat);
    const toDate = dayjs(this.toDate, defaultServerDateTimeFormat);
    const comparisonDate = dayjs('01/01/1901');

    const info =
      fromDate.isAfter(comparisonDate) && toDate.isAfter(comparisonDate)
        ? combineStrings(
            ' | ',
            lang[this.status],
            combineStrings(
              ' - ',
              localizeDate(fromDate, defaultDateTimeFormat),
              localizeDate(toDate, defaultDateTimeFormat)
            )
          )
        : combineStrings(' | ', lang[this.status], localizeDate(this.invoicingDate, 'MMMM YYYY'));

    return {
      id: this.id,
      title: this.dealer?.name || this.id.toString(),
      subtitle: this.contracts.map((it) => it.appNumberWithRevision).join(', '),
      info,
    };
  }

  static async list(body?: Api.Core.StatementBalance.List.IRequest) {
    const { payload, ...rest } = await statementBalanceListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new StatementBalance(it)),
        count: payload.count,
      },
    };
  }
  async save() {
    return await statementBalanceSaveApi(this);
  }
  async delete() {
    return await statementBalanceDeleteApi(this.id);
  }
}
